/**
 * LiveTakeoverBanner — iter-95
 *
 * Auto-appears on Home when a sports game is in status="live". Tapping
 * routes to the Rivalry Hub. Hidden when no game is live. Refreshes every
 * 60s so the banner appears/disappears organically across game day.
 *
 * Design posture: bold red pulse, minimal copy ("LIVE — Lakers vs Celtics"),
 * always one tap from the action. Compact enough to live above the
 * Home create input without dominating.
 */
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { API } from "../lib/api";

export default function LiveTakeoverBanner() {
    const [live, setLive] = useState(null);
    const navigate = useNavigate();

    useEffect(() => {
        let cancelled = false;
        const fetchLive = async () => {
            try {
                const { data } = await axios.get(`${API}/sports/live-takeover`);
                if (!cancelled) setLive(data?.live || null);
            } catch {
                if (!cancelled) setLive(null);
            }
        };
        fetchLive();
        const id = setInterval(fetchLive, 60000); // poll every 60s
        return () => { cancelled = true; clearInterval(id); };
    }, []);

    if (!live) return null;

    const away = live.away_team?.short || live.away_team?.full || "Away";
    const home = live.home_team?.short || live.home_team?.full || "Home";
    const awayColor = live.away_team?.primary_color || "#888";
    const homeColor = live.home_team?.primary_color || "#888";

    return (
        <AnimatePresence>
            <motion.button
                key={live.game_slug}
                initial={{ opacity: 0, y: -6 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -6 }}
                onClick={() => navigate(`/r/${live.game_slug}`)}
                data-testid="live-takeover-banner"
                className="w-full inline-flex items-center justify-between gap-3 py-2.5 px-4 rounded-full bg-rose-500/10 border border-rose-400/40 hover:bg-rose-500/15 transition-colors"
            >
                <div className="flex items-center gap-2.5">
                    <span className="relative flex h-2.5 w-2.5">
                        <span className="absolute inline-flex h-full w-full rounded-full bg-rose-400 opacity-60 animate-ping" />
                        <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-rose-400" />
                    </span>
                    <span className="font-sans text-[10px] uppercase tracking-[0.3em] text-rose-100 font-semibold">
                        live
                    </span>
                    <div className="flex items-center gap-1.5 font-sans text-[12px] text-app">
                        <span style={{ color: awayColor }} className="font-medium">{away}</span>
                        <span className="text-app-3">vs</span>
                        <span style={{ color: homeColor }} className="font-medium">{home}</span>
                    </div>
                </div>
                <span className="font-sans text-[10px] uppercase tracking-[0.28em] text-rose-200">
                    tap in →
                </span>
            </motion.button>
        </AnimatePresence>
    );
}
